/**
 * useExportAnalysis Hook
 *
 * Handles export logic for generating PDFs and images from HTML elements.
 * Extracts business logic from ExportAnalysisButton for reusability and testability.
 */

import { useState, useCallback, useMemo, type RefObject } from "react";
import { toast } from "sonner";

// ============================================================================
// Types
// ============================================================================

export type ExportFormat = "pdf" | "image";

export interface PdfOptions {
  title?: string;
  subtitle?: string;
}

export interface ExportOptions extends PdfOptions {
  fileName: string;
}

export interface UseExportAnalysisReturn {
  /** Export to PDF format */
  exportToPdf: (options?: ExportOptions) => Promise<void>;
  /** Export to image format (with Web Share API support) */
  exportToImage: (options?: ExportOptions) => Promise<void>;
  /** Export to specified format */
  exportTo: (format: ExportFormat, options?: ExportOptions) => Promise<void>;
  /** Whether an export is currently in progress */
  isExporting: boolean;
  /** The format currently being exported, if any */
  exportingFormat: ExportFormat | null;
  /** Whether the browser supports Web Share API for images */
  canShareImages: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_FILE_NAME = "recommendation-analysis";

const PDF_SETTINGS = {
  margin: 10,
  imageQuality: 0.95,
  format: "a4" as const,
};

// ============================================================================
// Web Share API Support (computed once at module load)
// ============================================================================

const CAN_SHARE = typeof navigator !== "undefined" && typeof navigator.share === "function";

/**
 * Check if the browser supports sharing files
 */
function canShareFiles(): boolean {
  if (!CAN_SHARE) return false;
  try {
    // Create a test file to check if file sharing is supported
    const testFile = new File(["test"], "test.png", { type: "image/png" });
    return Boolean(navigator.canShare?.({ files: [testFile] }));
  } catch {
    return false;
  }
}

const CAN_SHARE_FILES = canShareFiles();

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a timestamped filename
 */
function generateFileName(baseName: string, extension: string): string {
  const timestamp = new Date().toISOString().split("T")[0];
  const sanitizedName = baseName.replace(/[^a-zA-Z0-9-_]/g, "-").toLowerCase();
  return `${sanitizedName}-${timestamp}.${extension}`;
}

/**
 * Convert HTMLElement to canvas using html2canvas
 */
async function elementToCanvas(element: HTMLElement): Promise<HTMLCanvasElement> {
  const html2canvas = (await import("html2canvas")).default;

  const canvas = await html2canvas(element, {
    scale: 2, // Higher resolution
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
    scrollY: -window.scrollY,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });

  return canvas;
}

/**
 * Convert canvas to PDF and trigger download
 */
async function canvasToPdf(
  canvas: HTMLCanvasElement,
  fileName: string,
  title?: string,
  subtitle?: string
): Promise<void> {
  const { jsPDF } = await import("jspdf");

  const imgData = canvas.toDataURL("image/png", PDF_SETTINGS.imageQuality);
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: PDF_SETTINGS.format,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = PDF_SETTINGS.margin;
  const contentWidth = pageWidth - 2 * margin;

  let yOffset = margin;

  if (title) {
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text(title, margin, yOffset + 5);
    yOffset += 10;
  }

  if (subtitle) {
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100);
    pdf.text(subtitle, margin, yOffset + 3);
    pdf.setTextColor(0);
    yOffset += 8;
  }

  // Add generation date
  pdf.setFontSize(8);
  pdf.setTextColor(150);
  pdf.text(
    `Generated on ${new Date().toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })}`,
    margin,
    yOffset + 3
  );
  pdf.setTextColor(0);
  yOffset += 8;

  // Add separator line
  pdf.setDrawColor(200);
  pdf.line(margin, yOffset, pageWidth - margin, yOffset);
  yOffset += 5;

  // Calculate image dimensions to fit the page
  const imgWidth = contentWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  const availableHeight = pageHeight - yOffset - margin;
  const finalHeight = Math.min(imgHeight, availableHeight);
  const finalWidth = (finalHeight * canvas.width) / canvas.height;

  const xOffset = margin + (contentWidth - finalWidth) / 2;

  pdf.addImage(imgData, "PNG", xOffset, yOffset, finalWidth, finalHeight);

  // Add footer
  const footerY = pageHeight - 5;
  pdf.setFontSize(8);
  pdf.setTextColor(150);
  pdf.text("Generated by Curie Compass", margin, footerY);
  pdf.text("curie.money", pageWidth - margin - 20, footerY);

  pdf.save(fileName);
}

/**
 * Convert canvas to image blob
 */
async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create image blob"));
        }
      },
      "image/png",
      PDF_SETTINGS.imageQuality
    );
  });
}

/**
 * Share image using Web Share API
 */
async function shareImage(blob: Blob, fileName: string, title: string): Promise<boolean> {
  const file = new File([blob], fileName, { type: "image/png" });

  if (CAN_SHARE_FILES) {
    await navigator.share({
      files: [file],
      title,
      text: "Recommendation analysis from Curie Compass",
    });
    return true; // Shared successfully
  }
  return false; // Fallback needed
}

/**
 * Download blob as file
 */
function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useExportAnalysis
 *
 * Hook for exporting HTML elements as PDF or image.
 *
 * @param targetRef - Ref to the HTML element to export
 * @returns Export functions and state
 *
 * @example
 * ```tsx
 * function RecommendationDetail() {
 *   const contentRef = useRef<HTMLDivElement>(null);
 *   const { exportToPdf, exportToImage, isExporting } = useExportAnalysis(contentRef);
 *
 *   return (
 *     <div>
 *       <button onClick={() => exportToPdf({ fileName: 'analysis' })} disabled={isExporting}>
 *         Export PDF
 *       </button>
 *       <div ref={contentRef}>
 *         {/* Content to export *\/}
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export function useExportAnalysis(
  targetRef: RefObject<HTMLElement | null>
): UseExportAnalysisReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);

  const exportTo = useCallback(
    async (format: ExportFormat, options?: ExportOptions) => {
      if (isExporting || !targetRef.current) return;

      const fileName = options?.fileName ?? DEFAULT_FILE_NAME;

      setIsExporting(true);
      setExportingFormat(format);

      const toastId = toast.loading(
        format === "pdf" ? "Generating PDF..." : "Generating image...",
        { description: "Please wait while we prepare your export" }
      );

      try {
        const canvas = await elementToCanvas(targetRef.current);

        if (format === "pdf") {
          const pdfFileName = generateFileName(fileName, "pdf");
          await canvasToPdf(canvas, pdfFileName, options?.title, options?.subtitle);

          toast.success("PDF downloaded successfully!", {
            id: toastId,
            description: pdfFileName,
            duration: 4000,
          });
        } else {
          const imageFileName = generateFileName(fileName, "png");
          const blob = await canvasToBlob(canvas);

          const shared = await shareImage(
            blob,
            imageFileName,
            options?.title ?? "Recommendation Analysis"
          );

          if (shared) {
            toast.success("Ready to share!", {
              id: toastId,
              description: "Select where you want to share",
              duration: 3000,
            });
          } else {
            downloadBlob(blob, imageFileName);
            toast.success("Image downloaded successfully!", {
              id: toastId,
              description: imageFileName,
              duration: 4000,
            });
          }
        }
      } catch (error) {
        console.error("Export error:", error);
        const err = error instanceof Error ? error : new Error("Failed to export analysis");

        toast.error("Export failed", {
          id: toastId,
          description: err.message || "Please try again",
          duration: 4000,
        });
      } finally {
        setIsExporting(false);
        setExportingFormat(null);
      }
    },
    [isExporting, targetRef]
  );

  const exportToPdf = useCallback(
    (options?: ExportOptions) => exportTo("pdf", options),
    [exportTo]
  );

  const exportToImage = useCallback(
    (options?: ExportOptions) => exportTo("image", options),
    [exportTo]
  );

  return useMemo(
    () => ({
      exportToPdf,
      exportToImage,
      exportTo,
      isExporting,
      exportingFormat,
      canShareImages: CAN_SHARE_FILES,
    }),
    [exportToPdf, exportToImage, exportTo, isExporting, exportingFormat]
  );
}
